<template>
	<div class="oauth-authorize-container">
		<div class="loading-card" v-if="loading">
			<div class="spinner"></div>
			<p>{{ loadingMessage }}</p>
		</div>
		<div class="error-card" v-else-if="error">
			<h2>Authorization Error</h2>
			<p>{{ error }}</p>
			<button @click="goBack" class="btn-back">Go Back</button>
		</div>
		
		<!-- Consent Screen -->
		<div class="consent-card" v-else-if="showConsent">
			<h1>Authorize <span class="app-name">{{ clientName }}</span></h1>
			<p>This application is requesting access to your account. Select which permissions to grant:</p>
			
			<div class="org-notice" v-if="orgName">
				<strong>Connecting:</strong> {{ orgName }}
			</div>
			
			<div class="permissions">
				<h2>Permissions requested:</h2>
				<div class="scope-list">
					<label 
						v-for="scopeItem in availableScopes" 
						:key="scopeItem.scope" 
						class="scope-checkbox"
					>
						<input 
							type="checkbox" 
							v-model="selectedScopes" 
							:value="scopeItem.scope"
						>
						<span class="scope-label">{{ scopeItem.description }}</span>
					</label>
				</div>
			</div>
			
			<p class="scope-error" v-if="showScopeError">
				Please select at least one permission to continue.
			</p>
			
			<div class="buttons">
				<button class="btn-deny" @click="goBack">Cancel</button>
				<button 
					class="btn-allow" 
					@click="authorize"
					:disabled="selectedScopes.length === 0"
				>
					{{ selectedScopes.length === 0 ? 'Select Permissions' : 'Allow Selected' }}
				</button>
			</div>
			
			<p class="security-note">
				You can revoke access at any time from your account settings.
				This app will not have access to download file contents.
			</p>
		</div>
	</div>
</template>

<script>
import Base from '@/service/support/base-service';
import Auth from '@aws-amplify/auth';

const baseService = new Base();

// Scope definitions - must match backend SCOPES in oauth-lib.js
const SCOPE_DESCRIPTIONS = {
	'portal:read': 'List and view portal details',
	'portal:write': 'Create, update, and archive portals',
	'file:list': 'List file names and metadata in portals',
	'file:upload': 'Upload new files to portals',
	'task:read': 'List tasks and view task status',
	'task:write': 'Create, update, and complete tasks',
	'guest:invite': 'Invite guests to portals',
	'webhook:manage': 'Manage webhook subscriptions'
};

// Known platform clients - used to display friendly names
const PLATFORM_CLIENTS = {
	// Add your Zapier client ID here once you have it
	// 'your-zapier-client-id': 'Zapier'
};

export default {
	name: 'OAuthAuthorize',
	data() {
		return {
			loading: true,
			loadingMessage: 'Loading authorization...',
			error: null,
			showConsent: false,
			clientName: '',
			orgName: '',
			scopes: [],
			selectedScopes: [], // Scopes user has checked
			oauthParams: {},
			showScopeError: false
		};
	},
	computed: {
		availableScopes() {
			// If specific scopes are requested, show those
			if (this.scopes && this.scopes.length > 0) {
				return this.scopes.map(scope => ({
					scope: scope,
					description: SCOPE_DESCRIPTIONS[scope] || scope
				}));
			}
			// If no scopes specified, show all default scopes (Zapier typically doesn't send scope param)
			return Object.entries(SCOPE_DESCRIPTIONS).map(([scope, description]) => ({
				scope: scope,
				description: description
			}));
		}
	},
	mounted() {
		this.processOAuthRequest();
	},
	methods: {
		async processOAuthRequest() {
			try {
				// Get the OAuth parameters from the URL
				this.oauthParams = { ...this.$route.query };
				
				// Validate required parameters
				if (!this.oauthParams.client_id || !this.oauthParams.redirect_uri || !this.oauthParams.response_type) {
					this.error = 'Missing required OAuth parameters (client_id, redirect_uri, response_type)';
					this.loading = false;
					return;
				}
				
				// Parse scopes from URL
				this.scopes = (this.oauthParams.scope || '').split(' ').filter(s => s);
				
				// Initialize selectedScopes with all available scopes (pre-check all)
				if (this.scopes.length > 0) {
					// Use requested scopes
					this.selectedScopes = [...this.scopes];
				} else {
					// No scopes specified - pre-select all available scopes
					this.selectedScopes = Object.keys(SCOPE_DESCRIPTIONS);
				}
				
				// Get client name - check if it's a known platform client
				const clientId = this.oauthParams.client_id;
				if (PLATFORM_CLIENTS[clientId]) {
					this.clientName = PLATFORM_CLIENTS[clientId];
				} else {
					// Check if redirect_uri hints at the platform
					const redirectUri = this.oauthParams.redirect_uri || '';
					if (redirectUri.includes('zapier.com')) {
						this.clientName = 'Zapier';
					} else if (redirectUri.includes('make.com') || redirectUri.includes('integromat.com')) {
						this.clientName = 'Make (Integromat)';
					} else if (redirectUri.includes('n8n.io') || redirectUri.includes('n8n.cloud')) {
						this.clientName = 'n8n';
					} else {
						this.clientName = 'External Application';
					}
				}
				
				// Get organization name from store
				const org = baseService.store.state.organization;
				this.orgName = org ? org.name : '';
				
				// Show consent screen
				this.loading = false;
				this.showConsent = true;
				
			} catch (err) {
				console.error('OAuth authorization error:', err);
				this.error = 'An unexpected error occurred. Please try again.';
				this.loading = false;
			}
		},
		
		async authorize() {
			try {
				// Validate at least one scope is selected
				if (this.selectedScopes.length === 0) {
					this.showScopeError = true;
					return;
				}
				this.showScopeError = false;
				
				this.loading = true;
				this.showConsent = false;
				this.loadingMessage = 'Authorizing...';
				
				// Get the current session token
				const session = await Auth.currentSession();
				const idToken = session.getIdToken().getJwtToken();
				
				// Add consent=allow, approved_scopes, and response_format=json to get JSON response
				const params = { 
					...this.oauthParams, 
					consent: 'allow',
					response_format: 'json',
					approved_scopes: this.selectedScopes.join(',')
				};
				
				// Build query string
				const queryString = Object.keys(params)
					.map(key => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`)
					.join('&');
				
				// Get REST API endpoint from environment
				const apiBaseUrl = process.env.VUE_APP_aws_rest_api_endpoint || '';
				const authorizeUrl = `${apiBaseUrl}oauth/authorize?${queryString}`;
				
				// Call the API Gateway authorize endpoint with Cognito token
				const response = await fetch(authorizeUrl, {
					method: 'GET',
					headers: {
						'Accept': 'application/json',
						'Authorization': idToken
					}
				});
				
				if (!response.ok) {
					const errorText = await response.text();
					console.error('Authorization failed:', errorText);
					this.error = 'Authorization failed. Please try again.';
					this.loading = false;
					return;
				}
				
				const data = await response.json();
				
				// If we got a redirect URL in the response, redirect there
				if (data && data.redirect_url) {
					window.location.href = data.redirect_url;
				} else {
					this.error = 'Authorization failed. Please try again.';
					this.loading = false;
				}
				
			} catch (err) {
				console.error('Authorization error:', err);
				this.error = err.message || 'Authorization failed. Please try again.';
				this.loading = false;
			}
		},
		
		goBack() {
			// Redirect back to the OAuth client with an error
			if (this.oauthParams.redirect_uri) {
				const errorUrl = new URL(this.oauthParams.redirect_uri);
				errorUrl.searchParams.append('error', 'access_denied');
				errorUrl.searchParams.append('error_description', 'User denied the authorization request');
				if (this.oauthParams.state) {
					errorUrl.searchParams.append('state', this.oauthParams.state);
				}
				window.location.href = errorUrl.toString();
			} else {
				window.history.back();
			}
		}
	}
};
</script>

<style scoped>
.oauth-authorize-container {
	display: flex;
	align-items: center;
	justify-content: center;
	min-height: 100vh;
	background: #f5f5f5;
	padding: 20px;
}

.loading-card,
.error-card,
.consent-card {
	background: white;
	border-radius: 8px;
	box-shadow: 0 2px 10px rgba(0,0,0,0.1);
	padding: 40px;
	max-width: 500px;
	width: 100%;
}

.loading-card {
	text-align: center;
}

.spinner {
	width: 40px;
	height: 40px;
	border: 3px solid #e0e0e0;
	border-top-color: #0066cc;
	border-radius: 50%;
	animation: spin 1s linear infinite;
	margin: 0 auto 20px;
}

@keyframes spin {
	to {
		transform: rotate(360deg);
	}
}

.error-card {
	text-align: center;
}

.error-card h2 {
	color: #d32f2f;
	margin: 0 0 15px 0;
}

.error-card p {
	color: #666;
	margin: 0 0 20px 0;
}

.consent-card h1 {
	margin: 0 0 20px 0;
	font-size: 24px;
	color: #333;
}

.app-name {
	font-weight: bold;
	color: #0066cc;
}

.consent-card p {
	color: #666;
	line-height: 1.6;
	margin: 0 0 20px 0;
}

.org-notice {
	background: #e3f2fd;
	border: 1px solid #90caf9;
	border-radius: 4px;
	padding: 12px 16px;
	margin: 0 0 20px 0;
	color: #1565c0;
}

.permissions {
	background: #f9f9f9;
	border-radius: 4px;
	padding: 20px;
	margin: 20px 0;
}

.permissions h2 {
	margin: 0 0 15px 0;
	font-size: 16px;
	color: #333;
}

.scope-list {
	display: flex;
	flex-direction: column;
	gap: 12px;
}

.scope-checkbox {
	display: flex;
	align-items: flex-start;
	cursor: pointer;
}

.scope-checkbox input[type="checkbox"] {
	margin: 3px 12px 0 0;
	width: 18px;
	height: 18px;
	min-width: 18px;
	cursor: pointer;
	accent-color: #0066cc;
}

.scope-label {
	color: #555;
	line-height: 1.4;
}

.scope-checkbox:hover .scope-label {
	color: #333;
}

.scope-error {
	color: #d32f2f;
	font-size: 14px;
	margin: 0;
}

.security-note {
	font-size: 12px;
	color: #888;
	margin-top: 15px;
	padding-top: 15px;
	border-top: 1px solid #eee;
}

.buttons {
	display: flex;
	gap: 15px;
	margin-top: 30px;
}

.btn-back,
.btn-deny,
.btn-allow {
	flex: 1;
	padding: 12px 20px;
	border: none;
	border-radius: 4px;
	font-size: 16px;
	cursor: pointer;
	transition: all 0.2s;
}

.btn-back,
.btn-allow {
	background: #0066cc;
	color: white;
}

.btn-back:hover,
.btn-allow:hover:not(:disabled) {
	background: #0052a3;
}

.btn-allow:disabled {
	background: #ccc;
	cursor: not-allowed;
}

.btn-deny {
	background: #e0e0e0;
	color: #333;
}

.btn-deny:hover {
	background: #d0d0d0;
}
</style>
